import { useState, useEffect } from "react";
import {
  LegacyCard,
  ResourceList,
  ResourceItem,
  Avatar,
  Text,
} from "@shopify/polaris";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { useParams } from "react-router-dom";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetch } from "services/user-select.service";
import { addSigner } from "services/clicktosign.service";
import { signerEditErrorKey } from "utils/signer-edit-errors";
import { fetchAuthorizedFileDetails } from "containers/file-authorized/authorized-file.Slice";
import { fetchFuelReportDetails } from "containers/fuel-report-view-container/fuel-report-container.slice";

interface ConfirmModalProps {
  fileId: string;
  manager: string;
  signType: string;
  subject: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  fileId,
  manager,
  signType,
  subject,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId, reportId } = useParams();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectManagerUser, setSelectedManagerUser] = useState<any>("");

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const data = await fetch(companyId, {});
        const matchedUser = data.usersCollection.find(
          (user: any) => user.id === manager
        );
        if (matchedUser)
          setSelectedManagerUser({ ...matchedUser, type: t("manager") });
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [companyId]);

  const handleToSendRequest = async () => {
    try {
      setIsLoading(true);
      const payload = {
        fileId,
        manager,
        signType,
        subject,
      };
      await addSigner(companyId, payload);
      if (fileId && companyId) {
        dispatch(fetchFileDetails({ fileId, companyId }));
        dispatch(fetchAuthorizedFileDetails({ fileId, companyId }));
        if (reportId) {
          dispatch(fetchFuelReportDetails({ companyId, reportId }));
        }
      }

      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e) {
      setErrMsg(signerEditErrorKey(e, "error"));
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {!manager && (
          <div>
            <Banner tone="critical">
              {t("manager_is_not_assigned_to_the_user")}
            </Banner>
          </div>
        )}
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("Are_you_sure_you_want_to_send_document_signature_request")}
      </h1>

      <div style={{ margin: "10px 0" }}>
        <LegacyCard>
          <ResourceList
            resourceName={{ singular: "contact", plural: "contacts" }}
            items={[
              selectManagerUser && {
                id: selectManagerUser.id,
                name: selectManagerUser.name,
                email: selectManagerUser.email,
                type: selectManagerUser.type,
              },
            ].filter(Boolean)}
            renderItem={(item) => (
              <ResourceItem
                id={item.id}
                media={<Avatar customer name={item.name} />}
                accessibilityLabel={`View details for ${item.name}`}
                onClick={() => ""}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <Text as="h3" variant="headingMd">
                      {item.name}
                    </Text>
                    <Text as="h3">{item.email}</Text>
                  </div>
                  <div>
                    <Text as="h3" variant="bodySm">
                      {item.type}
                    </Text>
                  </div>
                </div>
              </ResourceItem>
            )}
          />
        </LegacyCard>
      </div>

      <InlineStack align="space-between">
        <Button
          onClick={handleToSendRequest}
          loading={isLoading}
          disabled={!manager}
        >
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
